import { Vendor } from "@shared/index";
import { vendorSchema } from "@shared/schemas";
import { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AUTH_PATHS, ROUTE_PATHS } from '@client-shared/lib/constants';
import * as api from '@client-shared/services/api';
import { handleDisplayErrorData } from '../../services/api/utilities/handleDisplayErrorData';
import { vendorLocalStorageKeys } from "../vendor/types";
import {
  clearRedirectUrl,
  handleLocalStorage,
  initialState,
  reducer,
  setAuthLoading,
  setInitialized,
  setRedirectUrl,
  setSFDCAuthTokens,
} from "./actions";
import useSfdcAuthInterceptor from "./SFDCAuthInterceptor";
import { HandleSetVendor, SFDCAuthActions, SFDCAuthContextState, sfdcLocalStorageKeys } from "./types";

const TOKEN_REFRESH_MAX_HOURS = 4;

export const TOKEN_REFRESH_LONG_TIMEOUT = 1000 * 60 * 60 * TOKEN_REFRESH_MAX_HOURS; // milliseconds * seconds * minutes * hours

export const AUTH_ERROR_LOGOUT_PATH = `${ROUTE_PATHS.LOGOUT}?logout-type=error`;
export const AUTH_NO_PREVIOUS_SESSION_LOGOUT_PATH = `${ROUTE_PATHS.LOGOUT}?logout-type=no-data`;

const defaultContextState: SFDCAuthContextState = [initialState, () => undefined];

export const SFDCAuthContext = React.createContext(defaultContextState);

type Props = {
  children: React.ReactNode;
};

export const SfdcAuthProvider: React.FC<Props> = ({ children }: Props) => {
  // get the current route
  const isAuthRoute = useIsGetAuthRoute();
  const [authState, authDispatch] = React.useReducer(reducer, initialState);
  const [isLogoutInitiated, setIsLogoutInitiated] = React.useState(false);
  const navigate = useNavigate();
  const tokenRefreshShortTimeOut = React.useRef<NodeJS.Timeout | null>(null);
  const isTokenFetchInitiatedRef = React.useRef<boolean>(false);
  const { i18n } = useTranslation();
  const translations = React.useMemo(() => i18n.t, [i18n.t]);

  // set
  const setResponseInterceptorRef = React.useRef<number | null>(null);
  const setRequestInterceptorRef = React.useRef<number | null>(null);

  // --------------------------------------------------------------------------------------------
  // methods ----------------------------------//
  // --------------------------------------------------------------------------------------------

  const navigateToLogoutWithError = React.useCallback(() => {
    navigate(AUTH_ERROR_LOGOUT_PATH);
  }, [navigate]);

  const navigateToLogout = React.useCallback(() => {
    navigate(ROUTE_PATHS.LOGOUT);
  }, [navigate]);

  const handleSetRequestInterceptor = React.useCallback(
    () => (set: () => number) => {
      setRequestInterceptorRef.current = set();
    },
    [setRequestInterceptorRef]
  );

  const handleSetResponseInterceptor = React.useCallback(
    (handleError: () => void, refreshTokenIfExpired: (token: string) => Promise<void>) =>
      (set: (handleError: () => void, refreshTokenIfExpired: (token: string) => Promise<void>) => number) => {
        setResponseInterceptorRef.current = set(handleError, refreshTokenIfExpired);
      },
    [setResponseInterceptorRef]
  );

  // --------------------------------------------------------------------------------------------
  // refresh the token
  const handleRefreshToken = React.useCallback(
    async (refreshToken: string | null | undefined) => {
      await refreshTokens(refreshToken, authDispatch, navigate);
    },
    [navigate]
  );

  const refreshTokenIfExpired = React.useCallback(
    async (refreshToken: string) => {
      await handleRefreshToken(refreshToken);
    },
    [handleRefreshToken]
  );

  // --------------------------------------------------------------------------------------------
  // setInterceptors

  const { handleSetAllInterceptors, handleEjectAllInterceptors } = useSfdcAuthInterceptor();

  const handleSetInterceptors = React.useCallback(() => {
    if (setRequestInterceptorRef.current || setResponseInterceptorRef.current) {
      handleEjectAllInterceptors(setRequestInterceptorRef.current, setResponseInterceptorRef.current);
    }

    handleSetAllInterceptors(handleSetRequestInterceptor(), handleSetResponseInterceptor(navigateToLogout, refreshTokenIfExpired));
  }, [
    handleSetAllInterceptors,
    handleSetRequestInterceptor,
    handleSetResponseInterceptor,
    navigateToLogout,
    handleEjectAllInterceptors,
    refreshTokenIfExpired,
  ]);

  const handleSetInterceptorsFromResponse = React.useCallback(
    (tokenResponse: api.TokenResponse) => {
      if (!tokenResponse?.idToken || !tokenResponse?.vendor?.vendorKey) {
        return navigateToLogoutWithError();
      }
      handleSetInterceptors();
    },
    [handleSetInterceptors, navigateToLogoutWithError]
  );

  const handleAuthErrorLogout = React.useCallback(() => {
    authDispatch(setSFDCAuthTokens(null, null, null));
    authDispatch(setInitialized());
    navigate(AUTH_ERROR_LOGOUT_PATH);
  }, [authDispatch, navigate]);

  // --------------------------------------------------------------------------------------------
  // fetch new tokens from the code
  const handleFetchNewTokens = React.useCallback(
    async (code: string) => {
      authDispatch(setAuthLoading(true));
      const sfdcAuthTokensResponse = await fetchTokens(code, handleAuthErrorLogout, translations);
      if (!sfdcAuthTokensResponse) return;

      authDispatch(
        setSFDCAuthTokens(sfdcAuthTokensResponse?.accessToken, sfdcAuthTokensResponse?.idToken, sfdcAuthTokensResponse?.refreshToken)
      );
      // set the interceptors
      handleSetInterceptorsFromResponse(sfdcAuthTokensResponse);
      return sfdcAuthTokensResponse.vendor;
    },
    [handleSetInterceptorsFromResponse, translations, handleAuthErrorLogout]
  );

  // --------------------------------------------------------------------------------------------
  // fetch the auth token from the code
  const handleFetchTokensOnAuth = React.useCallback(
    async (code: string, handleSetVendor: HandleSetVendor) => {
      if (authState.loading) return;
      // if tokens are already set then refresh the tokens
      if (authState.sfdcAuthToken && authState.sfdcIdToken && authState.sfdcRefreshToken && authState.initialized) {
        handleRefreshToken(authState.sfdcRefreshToken);
        navigate(authState.redirectUrl ?? "/");
        return authDispatch(setAuthLoading(false));
      }

      const sfdcAuthTokensResponseVendor = await handleFetchNewTokens(code);
      if (sfdcAuthTokensResponseVendor) {
        handleSetVendor(sfdcAuthTokensResponseVendor);
        authDispatch(setInitialized());
        navigate(authState.redirectUrl ?? "/");
        return authDispatch(setAuthLoading(false));
      }
    },
    [
      authState.loading,
      navigate,
      authState.sfdcAuthToken,
      authState.sfdcIdToken,
      authState.sfdcRefreshToken,
      authState.initialized,
      authState.redirectUrl,
      handleRefreshToken,
      authDispatch,
      handleFetchNewTokens,
    ]
  );

  // --------------------------------------------------------------------------------------------
  // get the auth tokens from local storage
  const getAuthTokensAndVendorFromLocalStorage = React.useCallback(() => {
    const vendorString = localStorage.getItem(vendorLocalStorageKeys.vendor);
    const vendor: Vendor | undefined = vendorString ? JSON.parse(vendorString) : undefined;
    return {
      sfdcAuthToken: localStorage.getItem(sfdcLocalStorageKeys.sfdcAuthToken),
      sfdcIdToken: localStorage.getItem(sfdcLocalStorageKeys.sfdcIdToken),
      refreshToken: localStorage.getItem(sfdcLocalStorageKeys.sfdcRefreshToken),
      vendor: vendor,
    };
  }, []);

  // --------------------------------------------------------------------------------------------
  // debounce the token refresh
  const handleDebouncedRefreshTokensLong = React.useCallback(() => {
    if (!authState.initialized || !authState.sfdcRefreshToken) return;
    if (tokenRefreshShortTimeOut.current) {
      clearTimeout(tokenRefreshShortTimeOut.current);
    }
    tokenRefreshShortTimeOut.current = setTimeout(() => {
      handleRefreshToken(authState.sfdcRefreshToken);
    }, TOKEN_REFRESH_LONG_TIMEOUT);
  }, [authState.initialized, authState.sfdcRefreshToken, handleRefreshToken]);

  const getRedirectUrlFromLocalStorage = React.useCallback(() => localStorage.getItem("redirectUrl"), []);

  // --------------------------------------------------------------------------------------------
  // When logout is called we clear the auth provider and the interceptor
  const handleClearAuthProvider = React.useCallback(() => {
    if (!authState.initialized) return;
    if (isLogoutInitiated) return;
    setIsLogoutInitiated(true);
    authDispatch(setSFDCAuthTokens(null, null, null));
    authDispatch(clearRedirectUrl());
  }, [authState.initialized, isLogoutInitiated, setIsLogoutInitiated]);

  // --------------------------------------------------------------------------------------------
  // Auth provider side effects
  // --------------------------------------------------------------------------------------------

  // --------------------------------------------------------------------------------------------
  // set the redirect url in local storage
  React.useEffect(() => {
    if (!authState.initialized || isLogoutInitiated) return;
    if (authState.redirectUrl) return;
    const redirectUrl = getRedirectUrlFromLocalStorage();
    if (redirectUrl) authDispatch(setRedirectUrl(redirectUrl));
  }, [authState.initialized, getRedirectUrlFromLocalStorage, authState.redirectUrl, isLogoutInitiated]);

  // --------------------------------------------------------------------------------------------
  // set the auth tokens from after tokens are set in state
  React.useEffect(() => {
    // if we are on an auth route then initialize the provider and stop
    if (isAuthRoute && !authState.initialized) return authDispatch(setInitialized());

    // Skip if already initiated in this session
    if (isTokenFetchInitiatedRef.current) return;

    if (isAuthRoute && authState.initialized) return;
    if (authState.initialized || isLogoutInitiated) return;
    if (authState.sfdcRefreshToken && authState.sfdcAuthToken && authState.sfdcIdToken) return;

    // Mark as initiated before fetching
    isTokenFetchInitiatedRef.current = true;

    // get the tokens from local storage
    const { sfdcAuthToken, sfdcIdToken, refreshToken, vendor } = getAuthTokensAndVendorFromLocalStorage();
    // for older sessions when no refresh token was saved we login again
    if (sfdcIdToken && sfdcAuthToken && !refreshToken) {
      return navigate(ROUTE_PATHS.LOGIN);
    }
    // if tokens or vendors are missing then redirect to login page
    if (!sfdcAuthToken || !sfdcIdToken || !refreshToken || !vendor?.vendorKey) {
      return navigate(ROUTE_PATHS.LOGIN);
    }
    authDispatch(setSFDCAuthTokens(sfdcAuthToken, sfdcIdToken, refreshToken));
    authDispatch(setInitialized());
    handleSetInterceptors();
  }, [
    authState.initialized,
    authState.sfdcRefreshToken,
    authState.sfdcAuthToken,
    authState.sfdcIdToken,
    getAuthTokensAndVendorFromLocalStorage,
    handleDebouncedRefreshTokensLong,
    navigate,
    authState.redirectUrl,
    isAuthRoute,
    isLogoutInitiated,
    authState,
    handleSetInterceptors,
  ]);

  // --------------------------------------------------------------------------------------------
  // once the tokens are set set the debounce in the background
  React.useEffect(() => {
    // Only set up refresh if we have both initialization and a refresh token
    if (!authState.initialized || !authState.sfdcRefreshToken) {
      return;
    }
    handleDebouncedRefreshTokensLong();
  }, [authState.initialized, authState.sfdcRefreshToken, handleDebouncedRefreshTokensLong, handleRefreshToken]);

  // --------------------------------------------------------------------------------------------
  // --------------------------------------------------------------------------------------------
  // --------------------------------------------------------------------------------------------
  // what is available to the provider's children
  // --------------------------------------------------------------------------------------------

  // context value
  const contextValue: SFDCAuthContextState = React.useMemo(
    () => [
      authState,
      authDispatch,
      {
        fetchTokensOnAuth: handleFetchTokensOnAuth,
        clearAuthProvider: handleClearAuthProvider,
      },
    ],
    [authState, handleFetchTokensOnAuth, handleClearAuthProvider, authDispatch]
  );

  // --------------------------------------------------------------------------------------------
  // --------------------------------------------------------------------------------------------
  // if auth state is initialized then provide the context
  // otherwise return the AppContainerNoAuth with loading
  return <>{authState.initialized ? <SFDCAuthContext.Provider value={contextValue}>{children}</SFDCAuthContext.Provider> : null}</>;
};

// ====================================================================================================================
// side effects ----------------------------------//
// ====================================================================================================================

const fetchTokens = async (code: string, logOut: () => void, translations: TFunction<"translation", undefined>) => {
  try {
    const tokenResponse = await api.getOAuthToken(code);
    if (tokenResponse?.vendor) {
      const vendorValidation = await vendorSchema.safeParseAsync(tokenResponse.vendor);
      if (!vendorValidation.success) {
        console.error("Error retrieving Vendor details", vendorValidation.error);
        return handleDisplayErrorData(
          translations("apiErrorMessages.getVendorDetails.message"),
          translations("apiErrorMessages.getVendorDetails.title"),
          undefined,
          logOut,
          2000
        );
      }
    }
    return tokenResponse;
  } catch (error) {
    console.error("Error retrieving OAuth token", error);
    logOut();
  }
};

const refreshTokens = async (
  refreshToken: string | undefined | null,
  authDispatch: (action: SFDCAuthActions) => void,
  navigate: (path: string) => void
) => {
  if (!refreshToken) {
    console.error("No refresh token found. Logging out...");
    return navigate(ROUTE_PATHS.LOGOUT);
  }
  try {
    const tokenResponse = await api.refreshToken(refreshToken);
    if (!tokenResponse?.idToken || !tokenResponse?.vendor?.vendorKey) {
      navigate(ROUTE_PATHS.LOGOUT);
    }
    handleLocalStorage(sfdcLocalStorageKeys.sfdcIdToken, tokenResponse.idToken);
    authDispatch(setSFDCAuthTokens(tokenResponse.accessToken, tokenResponse.idToken, tokenResponse.refreshToken));
  } catch (error) {
    console.error("Error refreshing tokens", error);
    navigate(ROUTE_PATHS.LOGOUT);
  }

  return authDispatch(setInitialized());
};

// ====================================================================================================================
// useIsAuthRoute
// ====================================================================================================================

export const getIsAuthRoute = (pathname: string) => {
  const isAuthRoute = AUTH_PATHS.includes(pathname.split("?")[0] as string);
  return isAuthRoute;
};

export const useIsGetAuthRoute = () => {
  const location = useLocation();
  return getIsAuthRoute(location.pathname);
};
